import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import {
  BimplusSignalrClient,
  SignalRClientConfiguration,
  SignalrData,
  TransportType,
} from 'bimplus-signalr-client';
import { UserEventBus } from '@web-portal/domains/shared/event-bus';
import { BIMPLUS_API, BIMPLUS_API_CONFIG } from '@web-portal/domains/shared/bimplus-core';

export type SignalrClientFactory = (config: SignalRClientConfiguration) => BimplusSignalrClient;

export const SIGNALR_CLIENT_FACTORY = new InjectionToken<SignalrClientFactory>(
  'UserSignalrClientFactory',
  { providedIn: 'root', factory: () => config => new BimplusSignalrClient(config) }
);

@Injectable()
export class UserSignalrService {
  private readonly api = inject(BIMPLUS_API);
  private readonly apiConfig = inject(BIMPLUS_API_CONFIG);
  private readonly userEventBus = inject(UserEventBus);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clientFactory = inject(SIGNALR_CLIENT_FACTORY);

  private client: BimplusSignalrClient | null = null;

  init() {
    this.client = this.clientFactory({
      hubName: 'UserHub',
      baseUrl: `${this.apiConfig.protocol}${this.apiConfig.host}/`,
      signalrTransports: [TransportType.WebSockets],
      useFirefoxWorkaround: false,
      options: {
        skipNegotiation: true,
      },
      reconnect: () => this.onReconnect(),
      eventsList: [
        {
          eventName: 'ImportProgress',
          eventHandler: (data: SignalrData) =>
            this.userEventBus.emit({ type: '[User] import-progress', payload: data }),
        },
        {
          eventName: 'ExportProgress',
          eventHandler: (data: SignalrData) =>
            this.userEventBus.emit({ type: '[User] export-progress', payload: data }),
        },
        {
          eventName: 'ClashDetectionProgress',
          eventHandler: (data: SignalrData) =>
            this.userEventBus.emit({ type: '[User] clash-detection-progress', payload: data }),
        },
        {
          eventName: 'SendMessage',
          eventHandler: (data: SignalrData) =>
            this.userEventBus.emit({ type: '[User] send-message', payload: data }),
        },
      ],
    });

    this.client.initialize(() => this.api.getAccessToken());

    this.client.connect().catch(err => console.error('UserHub connection failed:', err));

    this.destroyRef.onDestroy(() => this.client?.disconnect());
  }

  private onReconnect(): void {
    this.client?.connect().catch(err => console.error('UserHub reconnection failed:', err));
  }
}
